/**
 * The surface of Noppenschaum, as a function.
 *
 * One definition, read from two places: `createSpikedPadGeometry` displaces a
 * mesh with it, and `FoamMaterials` bakes it into the maps that pads with
 * cutouts wear instead. Both are the same product and have to agree on what a
 * nub looks like.
 */

/**
 * Abstand zweier Noppenspitzen, in mm.
 *
 * Convoluted foam is cut by rolling the block through a pair of meshing
 * profiled rollers, so the nubs sit on the roller's tooth pitch. 25 mm is what
 * the sheets in the catalogue carry.
 */
export const SPIKE_PITCH_MM = 25;

/**
 * Dicke der geschlossenen Grundplatte unter den Noppen, in mm.
 *
 * From the product description: `Noppenschaumstoff 20/10` on a 30 mm pad is a
 * 20 mm nub standing on a 10 mm base. The nub height is therefore not a free
 * parameter — it is whatever is left of the pad above the base.
 */
export const SPIKE_BASE_MM = 10;

/**
 * Höhe der Noppen des Katalogblatts, in mm.
 *
 * Only for the painted-on version, which has no thickness to read the nub
 * height off. The geometry takes it from the pad.
 */
export const SPIKE_NUB_MM = 20;

/**
 * How pointed the nubs are, in [0, 1).
 *
 * The profile below is a sum of cosines, which by itself gives a nub as round
 * as a dome. The nubs in the catalogue photographs are conical with a blunt
 * tip: the flank is steepest near the top and eases out into the saddle. The
 * cosine is reshaped by `f + s (f³ - f)`, which leaves the tips and valleys
 * where they are, steepens the flank next to them by `1 + 2s` and flattens
 * the saddles by `1 - s`. It is odd, so the complement property of the
 * profile survives it, and smooth everywhere — a power curve did the same job
 * but had zero slope exactly on the saddle contour, and the maps baked from it
 * carried that as a thin flat line along every diagonal.
 *
 * `0` is the pure cosine; the concave exponent from before this profile
 * existed made the tip a needle, which read as a bed of nails.
 */
export const SPIKE_SHARPNESS = 0.4;

/**
 * How dark the deepest valley between four nubs goes, as a fraction of the
 * ambient light taken away.
 *
 * The product scenes are lit by an environment map alone, and an environment
 * map cannot cast a shadow: a valley would be lit exactly as brightly as the
 * tip above it. Screen-space occlusion cannot account for off-screen nubs
 * and loses detail at distance, so the horizon is also computed from the
 * surface itself. The current viewer reaches 50 mm and overlaps these
 * cavities; the modelled-foam material weights this bake rather than changing
 * the physical horizon or weakening the painted cutout fallback.
 *
 * Not quite the full computed figure, since foam scatters some light into its
 * own hollows that a horizon test cannot see.
 */
export const SPIKE_OCCLUSION_STRENGTH = 0.85;

/**
 * Azimuths the occlusion test looks along, and steps it takes along each.
 *
 * Eight directions cover the four neighbouring nubs and the four saddles
 * between them once each; eight steps over one pitch is a sample every 3 mm
 * against features 12 mm apart.
 */
const OCCLUSION_DIRECTIONS = 8;
const OCCLUSION_STEPS = 8;

const TWO_PI = Math.PI * 2;

// Keep the original trig results, including cardinal-axis residuals: snapping
// them to zero can change which samples lie beyond the rim.
const OCCLUSION_VECTORS = Array.from(
  { length: OCCLUSION_DIRECTIONS },
  (_, d): readonly [number, number] => {
    const azimuth = (d / OCCLUSION_DIRECTIONS) * TWO_PI;
    return [Math.cos(azimuth), Math.sin(azimuth)];
  },
);

/**
 * Nub height at `(x, z)`, normalised: 1 on a tip, 0 at the bottom of a valley.
 *
 * Tips sit at the centres of `pitch` cells measured from `x = 0` and `z = 0`,
 * the valleys at the cell corners, and the saddle between two neighbouring tips
 * halfway up. That is the shape a pair of meshing rollers leaves, and it can be
 * checked: both halves of the block are sold as the same sheet, so the profile
 * has to be its own complement. Turn it upside down and shift it half a pitch
 * along both axes, and it must come back to itself. A sum of cosines does;
 * the pyramid this replaced (a square footprint with full-depth trenches along
 * both axes between every pair of nubs) does not, so it was the wrong shape
 * in kind.
 */
export function noppenHeight(
  x: number,
  z: number,
  pitchX: number,
  pitchZ: number,
): number {
  const field =
    -0.5 * (Math.cos((TWO_PI * x) / pitchX) + Math.cos((TWO_PI * z) / pitchZ));
  return 0.5 + 0.5 * sharpen(field);
}

/**
 * Gradient of {@link noppenHeight}, in normalised height per millimetre.
 *
 * Exact, which is what makes it worth having: averaging the faces around a
 * vertex understated the flank slope by about six degrees at the resolution
 * the geometry is built at, and could not have been made exact by more
 * subdivision without paying for it at every frame.
 */
export function noppenSlope(
  x: number,
  z: number,
  pitchX: number,
  pitchZ: number,
): [number, number] {
  const ax = (TWO_PI * x) / pitchX;
  const az = (TWO_PI * z) / pitchZ;
  const field = -0.5 * (Math.cos(ax) + Math.cos(az));
  const gain = 0.5 * sharpenSlope(field);
  return [
    gain * (Math.PI / pitchX) * Math.sin(ax),
    gain * (Math.PI / pitchZ) * Math.sin(az),
  ];
}

/** Odd reshaping of [-1, 1] onto itself; see {@link SPIKE_SHARPNESS}. */
function sharpen(field: number): number {
  return field + SPIKE_SHARPNESS * (field * field * field - field);
}

function sharpenSlope(field: number): number {
  return 1 - SPIKE_SHARPNESS + 3 * SPIKE_SHARPNESS * field * field;
}

/**
 * Extent of the surface, in mm from the origin along each axis. Beyond it the
 * surface is cut away and nothing there occludes.
 */
export type SurfaceBounds = Readonly<{ width: number; depth: number }>;

/**
 * Horizon occlusion of the nub surface at `(x, z)`, in [0, 1]: 1 where the sky
 * is open, lower where the neighbouring nubs close over it.
 *
 * Along each of {@link OCCLUSION_DIRECTIONS} azimuths the highest elevation
 * the surface reaches within one pitch is found, measured against the tangent
 * plane at the point rather than the horizontal — a flank is not occluded by
 * its own slope. The visible fraction of a cosine-weighted hemisphere above a
 * horizon at elevation `θ` is `cos²θ`, averaged over the azimuths.
 *
 * @param spike Nub height in mm, which sets how steep the surface is.
 * @param bounds Where the surface ends; samples beyond it see open air.
 */
export function noppenOcclusion(
  x: number,
  z: number,
  pitchX: number,
  pitchZ: number,
  spike: number,
  bounds?: SurfaceBounds,
): number {
  const here = spike * noppenHeight(x, z, pitchX, pitchZ);
  const [slopeX, slopeZ] = noppenSlope(x, z, pitchX, pitchZ);
  const reach = Math.max(pitchX, pitchZ);

  let visible = 0;
  for (const [dx, dz] of OCCLUSION_VECTORS) {
    const plane = spike * (slopeX * dx + slopeZ * dz);

    let horizon = plane;
    for (let s = 1; s <= OCCLUSION_STEPS; s++) {
      const r = (s / OCCLUSION_STEPS) * reach;
      const px = x + dx * r;
      const pz = z + dz * r;
      if (
        bounds &&
        (px < 0 || px > bounds.width || pz < 0 || pz > bounds.depth)
      ) {
        break;
      }
      const rise = (spike * noppenHeight(px, pz, pitchX, pitchZ) - here) / r;
      if (rise > horizon) horizon = rise;
    }

    // An unchanged finite horizon has exactly zero elevation and unit sky.
    // Leave nonfinite inputs on the original arithmetic path.
    if (horizon === plane && Number.isFinite(plane)) {
      visible += 1;
      continue;
    }

    const elevation = Math.atan(horizon) - Math.atan(plane);
    // Clamped at zero, not just squared: a nub taller than the sample point is
    // steeper than vertical relative to that point's own tangent plane, which
    // puts the elevation past 90 degrees and the cosine below zero. Squaring a
    // negative cosine hands a fully blocked direction back as visible sky, so
    // the deepest valley next to a flank — the darkest place on the surface —
    // is exactly where the bake would brighten, and the error grows with the
    // spike-to-pitch ratio.
    const open = Math.max(0, Math.cos(elevation));
    visible += open * open;
  }

  return visible / OCCLUSION_DIRECTIONS;
}

/** {@link noppenOcclusion}, scaled by {@link SPIKE_OCCLUSION_STRENGTH}. */
export function noppenShade(
  x: number,
  z: number,
  pitchX: number,
  pitchZ: number,
  spike: number,
  bounds?: SurfaceBounds,
): number {
  return (
    1 -
    SPIKE_OCCLUSION_STRENGTH *
      (1 - noppenOcclusion(x, z, pitchX, pitchZ, spike, bounds))
  );
}
